import { TrackingEvents, track } from "#webapp/src/util/analytics.util";
import { useQuery } from "@apollo/client";
import { getValue } from "@clockwise/client-commons/src/util/errorable.util";
import { isNotNull } from "@clockwise/client-commons/src/util/null";
import {
  CalendarSelectPeople,
  CalendarSelectPeopleOption,
} from "@clockwise/web-commons/src/components/calendar/calendar-select-people/CalendarSelectPeople";
import { border_calOwn } from "@clockwise/web-commons/src/constants/calendarColorTokens";
import { useReadCalendarColors } from "@clockwise/web-commons/src/util/CalendarColorsContext";
import { generateScheduleCQL } from "@clockwise/web-commons/src/util/generateCQL";
import { noop, union, uniq } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getCurrentOrg } from "../../../util/org.util";
import { getFullName } from "../../admin-panel/admin-panel-view/ap-members/ui/members-content.util";
import { QUERYING_LIMIT_OF_CALENDARS_TO_SHOW_IN_PLANNER } from "../../chat/ai-chat/components/multi-calendar/calendar-layers.util";
import { useFocusChatTextInput } from "../../chat/ai-chat/hooks/useFocusChatTextInput";
import { useProcessMessage } from "../../chat/ai-chat/hooks/useProcessMessage";
import { useUserProfile } from "../../hooks/useUserProfile";
import { MultiCalendarSelectQueryDocument } from "./graphql/__generated__/MultiCalendarSelect.generated";
import {
  CalendarProfile,
  useFetchMultiCalendarSelectProfiles,
} from "./util/MultiCalendarSelect.util";

const MAX_SELECTIONS = QUERYING_LIMIT_OF_CALENDARS_TO_SHOW_IN_PLANNER;

type Props = {
  onMultiCalAdd: (calendarIds: string[]) => void;
  onMultiCalRemove: (calendarIds: string[]) => void;
  selectedCalendarIds: string[];
  previewedCalendarId: string[];
};

export const MultiCalendarSelect = ({
  onMultiCalAdd = noop,
  onMultiCalRemove = noop,
  previewedCalendarId = [],
  selectedCalendarIds = [],
}: Props) => {
  const focusChatTextInput = useFocusChatTextInput();
  const { processMessage } = useProcessMessage(focusChatTextInput);
  const [query, setQuery] = useState("");
  const { userProfile: selfProfile } = useUserProfile();
  const calendarIds = uniq([...selectedCalendarIds, ...previewedCalendarId]);
  const calendarColors = useReadCalendarColors();
  const { data } = useQuery(MultiCalendarSelectQueryDocument);
  const viewer = getValue(data?.viewer, "Viewer");
  const org = getCurrentOrg(viewer);
  const ordId = org?.id || "";
  const knownProfiles = useRef<Map<string, CalendarProfile>>(new Map());
  const atMax = selectedCalendarIds.length + previewedCalendarId.length >= MAX_SELECTIONS;

  const {
    calendarProfilesByCalendarIds,
    calendarProfilesFromSearch,
    calendarProfilesFromActiveEvent,
    calendarProfilesFromTopCollaborators,
  } = useFetchMultiCalendarSelectProfiles(ordId, calendarIds, query);

  useEffect(() => {
    calendarProfilesByCalendarIds.forEach((profile) => {
      knownProfiles.current.set(profile.primaryCalendar, profile);
    });
  }, [calendarProfilesByCalendarIds, knownProfiles]);

  useEffect(() => {
    calendarProfilesFromSearch.forEach((profile) => {
      knownProfiles.current.set(profile.primaryCalendar, profile);
    });
  }, [calendarProfilesFromSearch, knownProfiles]);

  useEffect(() => {
    calendarProfilesFromActiveEvent.forEach((profile) => {
      knownProfiles.current.set(profile.primaryCalendar, profile);
    });
  }, [calendarProfilesFromActiveEvent, knownProfiles]);

  useEffect(() => {
    calendarProfilesFromTopCollaborators.forEach((profile) => {
      knownProfiles.current.set(profile.primaryCalendar, profile);
    });
  }, [calendarProfilesFromTopCollaborators, knownProfiles]);

  const selections = useMemo<CalendarSelectPeopleOption[]>(() => {
    return selectedCalendarIds
      .filter((selectedId) => selectedId !== selfProfile.primaryEmail)
      .sort((a, b) => {
        const aProfile = knownProfiles.current.get(a);
        const bProfile = knownProfiles.current.get(b);
        return sortProfiles(aProfile, bProfile);
      })
      .map((calendarId) => {
        const matchProfile = knownProfiles.current.get(calendarId);

        return {
          calendarId,
          color: calendarColors[calendarId]?.border || "#000000",
          profile: {
            givenName: matchProfile?.profile?.givenName || "",
            familyName: matchProfile?.profile?.familyName || "",
            externalImageUrl: matchProfile?.profile?.externalImageUrl || "",
          },
          selectionState: "selected-active",
        };
      });
  }, [calendarColors, selectedCalendarIds, selfProfile.primaryEmail]);

  const previews = useMemo<CalendarSelectPeopleOption[]>(() => {
    return previewedCalendarId
      .filter((previewId) => previewId !== selfProfile.primaryEmail)
      .filter((previewId) => !selectedCalendarIds.includes(previewId))
      .sort((a, b) => {
        const aProfile = knownProfiles.current.get(a);
        const bProfile = knownProfiles.current.get(b);
        return sortProfiles(aProfile, bProfile);
      })
      .map((calendarId) => {
        const matchProfile = knownProfiles.current.get(calendarId);

        return {
          calendarId,
          color: calendarColors[calendarId]?.border || "#000000",
          profile: {
            givenName: matchProfile?.profile?.givenName || "",
            familyName: matchProfile?.profile?.familyName || "",
            externalImageUrl: matchProfile?.profile?.externalImageUrl || "",
          },
          selectionState: "selected-inactive",
        };
      });
  }, [previewedCalendarId, selfProfile.primaryEmail, selectedCalendarIds, calendarColors]);

  const suggestions = useMemo<CalendarSelectPeopleOption[]>(() => {
    return calendarProfilesFromSearch
      .filter(({ profile }) => isNotNull(profile))
      .filter(({ primaryCalendar }) => !selectedCalendarIds.includes(primaryCalendar))
      .filter(({ primaryCalendar }) => !previewedCalendarId.includes(primaryCalendar))
      .sort(sortProfiles)
      .map((calProfile) => ({
        calendarId: calProfile.primaryCalendar,
        color: "#000000",
        profile: {
          givenName: calProfile.profile?.givenName,
          familyName: calProfile.profile?.familyName,
          externalImageUrl: calProfile.profile?.externalImageUrl,
        },
        selectionState: "suggested",
      }));
  }, [calendarProfilesFromSearch, previewedCalendarId, selectedCalendarIds]);

  const handleOpenClose = (open: boolean) => {
    track(TrackingEvents.MULTI_CALENDAR_SELECT.BUTTON_CLICKED, { open });
  };

  const handleSelect = (calendarId: string) => {
    if (atMax) {
      return;
    }

    if (selectedCalendarIds.length + 1 >= MAX_SELECTIONS) {
      track(TrackingEvents.MULTI_CALENDAR_SELECT.MAX_REACHED, {});
    }

    onMultiCalAdd([calendarId]);
    track(TrackingEvents.MULTI_CALENDAR_SELECT.CALENDAR_TOGGLED, {
      primaryCalendar: calendarId,
      checked: true,
    });
  };

  const handleUnselect = (calendarId: string) => {
    onMultiCalRemove([calendarId]);
    track(TrackingEvents.MULTI_CALENDAR_SELECT.CALENDAR_TOGGLED, {
      primaryCalendar: calendarId,
      checked: false,
    });
  };

  const handleClear = () => {
    onMultiCalRemove(selectedCalendarIds);
    track(TrackingEvents.MULTI_CALENDAR_SELECT.CLEAR, {});
  };

  const handleAutoSchedule = () => {
    const formatter = new Intl.ListFormat("en", { style: "long", type: "conjunction" });
    const namesArr = selections.map(({ profile }) =>
      getFullName({ givenName: profile?.givenName || "", familyName: profile?.familyName || "" }),
    );
    const names = formatter.format(namesArr);
    const calendarIds = selections.map((selection) => selection.calendarId);
    const cql = generateScheduleCQL(calendarIds);
    void processMessage(
      `Schedule with ${names}`,
      {
        eventMentions: [],
        personMentions: [],
      },
      cql,
    );

    track(TrackingEvents.MULTI_CALENDAR_SELECT.SCHEDULE, {
      primaryCalendarIds: selections.map((person) => person.calendarId),
    });
  };

  const message = atMax
    ? `Showing the maximum number of calendars at once (${MAX_SELECTIONS}). Remove a calendar to show more.`
    : undefined;

  const options = useMemo<CalendarSelectPeopleOption[]>(() => {
    const self: CalendarSelectPeopleOption = {
      calendarId: selfProfile?.primaryEmail || "",
      color: border_calOwn,
      profile: selfProfile,
      selectionState: "selected-fixed",
    };

    return union([...selections, ...suggestions, ...previews, self]);
  }, [previews, selections, selfProfile, suggestions]);

  return (
    <CalendarSelectPeople
      message={message}
      onOpenClose={handleOpenClose}
      onAutoSchedule={handleAutoSchedule}
      onSearchChange={setQuery}
      onSelect={handleSelect}
      onSelectionsClear={handleClear}
      onUnselect={handleUnselect}
      search={query}
      searchLoading={false}
      options={options}
    />
  );
};

const sortProfiles = (a?: CalendarProfile, b?: CalendarProfile) => {
  if (!a || !b) return 0;
  if (!a) return 1;
  if (!b) return -1;

  return (
    a.profile?.givenName?.localeCompare(b.profile?.givenName || "") ||
    a.profile?.familyName?.localeCompare(b.profile?.familyName || "") ||
    a.primaryCalendar.localeCompare(b.primaryCalendar) ||
    0
  );
};
